/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Dynamic Template Form Component from JSON Schema (Task 10.2.6 & Phase 4 Task 4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import {
  RotateCcw,
  Eye,
  EyeOff,
  AlertCircle,
  Cpu,
  SlidersHorizontal,
  ChevronDown,
  Info,
  Globe,
  Gauge,
  Smile,
  Tag,
  X,
  Plus,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import type { PromptTemplateDto, PromptFieldDto } from '@sofia/shared-types';
import {
  PromptExecutionService,
  OUTPUT_LANGUAGES,
  OUTPUT_LANGUAGE_CODES,
  type OutputLanguageOption,
} from '../../services/prompt-execution.service';
import { useI18n } from '../../hooks/useI18n';
import type { SupportedLocale } from '../../i18n/types';

export { OUTPUT_LANGUAGES, OUTPUT_LANGUAGE_CODES, type OutputLanguageOption };

export const TONES_OF_VOICE = [
  'casual',
  'humorous',
  'friendly',
  'professional',
  'serious',
  'elegant',
  'confident',
  'optimistic',
  'encouraging',
  'witty',
  'bold',
  'calm',
  'energetic',
  'sarcastic',
  'formal',
  'playful',
  'romantic',
  'luxurious',
  'mysterious',
  'adventurous',
  'vintage',
  'futuristic',
  'natural',
  'dramatic',
  'gentle',
  'technical',
  'persuasive',
] as const;

export type OutputLengthTier = 'short' | 'medium' | 'long' | 'custom';

export interface DynamicFormConfig {
  model: string;
  temperature: number;
  outputLanguage: string;
  outputLength: OutputLengthTier;
  maxTokens: number;
  toneOfVoice: string;
  focusKeywords: string[];
}

interface DynamicTemplateFormProps {
  template: PromptTemplateDto;
  isGenerating?: boolean;
  onSubmit: (values: Record<string, unknown>, config: DynamicFormConfig) => void;
  onReset?: () => void;
}

export const DynamicTemplateForm: React.FC<DynamicTemplateFormProps> = ({
  template,
  isGenerating = false,
  onSubmit,
  onReset,
}) => {
  const { t, currentLocale } = useI18n();

  // 1. Initialize dynamic schema values
  const [formValues, setFormValues] = useState<Record<string, unknown>>(() =>
    PromptExecutionService.getInitialValues(template.inputSchema)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // 2. Standardized AI Writer Parameters (Phase 4 Task 4.1)
  const [outputLanguage, setOutputLanguage] = useState<string>(() => {
    return OUTPUT_LANGUAGE_CODES.includes(currentLocale as SupportedLocale) ? currentLocale : 'en';
  });
  const [outputLength, setOutputLength] = useState<OutputLengthTier>('medium');
  const [customMaxTokens, setCustomMaxTokens] = useState<number>(800);
  const [toneOfVoice, setToneOfVoice] = useState<string>('professional');

  // Focus Keywords tag list
  const [focusKeywords, setFocusKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState<string>('');

  // 3. Model & Creativity configurations
  const [selectedModel, setSelectedModel] = useState<string>(
    template.defaultModelId?.includes('claude')
      ? 'claude-3-5-sonnet'
      : template.defaultModelId?.includes('deepseek')
      ? 'deepseek-r1'
      : 'gpt-4o'
  );
  const [temperature, setTemperature] = useState<number>(0.7);
  const [showPromptPreview, setShowPromptPreview] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  // Field change handler
  const handleFieldChange = (name: string, value: unknown) => {
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      if (touched[name]) {
        const valRes = PromptExecutionService.validateForm(template.inputSchema, next);
        setErrors((prevErr) => {
          const nextErr = { ...prevErr };
          if (!valRes.errors[name]) {
            delete nextErr[name];
          } else {
            nextErr[name] = valRes.errors[name];
          }
          return nextErr;
        });
      }
      return next;
    });
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const valRes = PromptExecutionService.validateForm(template.inputSchema, formValues);
    setErrors(valRes.errors);
  };

  // Keyword tags handling
  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !focusKeywords.includes(trimmed)) {
      setFocusKeywords((prev) => [...prev, trimmed]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setFocusKeywords((prev) => prev.filter((k) => k !== keywordToRemove));
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Resolve token budget from selected length
  const effectiveMaxTokens = useMemo(() => {
    switch (outputLength) {
      case 'short':
        return 400;
      case 'medium':
        return 1000;
      case 'long':
        return 2200;
      case 'custom':
        return customMaxTokens;
      default:
        return 1000;
    }
  }, [outputLength, customMaxTokens]);

  // Submit handler with full schema validation
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const allTouched: Record<string, boolean> = {};
    template.inputSchema.forEach((f) => {
      allTouched[f.name] = true;
    });
    setTouched(allTouched);

    const validation = PromptExecutionService.validateForm(template.inputSchema, formValues);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    onSubmit(formValues, {
      model: selectedModel,
      temperature,
      outputLanguage,
      outputLength,
      maxTokens: effectiveMaxTokens,
      toneOfVoice,
      focusKeywords,
    });
  };

  const handleResetForm = () => {
    const defaults = PromptExecutionService.getInitialValues(template.inputSchema);
    setFormValues(defaults);
    setFocusKeywords([]);
    setKeywordInput('');
    setOutputLength('medium');
    setCustomMaxTokens(800);
    setToneOfVoice('professional');
    setErrors({});
    setTouched({});
    if (onReset) onReset();
  };

  // Compute live interpolated prompt preview
  const livePreview = useMemo(() => {
    try {
      return PromptExecutionService.interpolatePrompts(template, formValues, {
        outputLanguage,
        outputLength,
        maxTokens: effectiveMaxTokens,
        toneOfVoice,
        focusKeywords,
      });
    } catch {
      return { systemPrompt: template.systemPrompt, userPrompt: template.promptTemplate };
    }
  }, [template, formValues, outputLanguage, outputLength, effectiveMaxTokens, toneOfVoice, focusKeywords]);

  // Render individual input field based on its JSON schema type
  const renderField = (field: PromptFieldDto) => {
    const val = formValues[field.name];
    const hasError = !!(touched[field.name] && errors[field.name]);
    const errorMsg = errors[field.name];

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.name} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-rose-500">*</span>}
              </label>
              {field.validation?.max && (
                <span className="text-[10px] text-foreground-muted font-mono">
                  {String(val || '').length} / {field.validation.max}
                </span>
              )}
            </div>
            {field.description && (
              <p className="text-[11px] text-foreground-muted">{field.description}</p>
            )}
            <textarea
              rows={3}
              value={String(val || '')}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              placeholder={field.placeholder || t('templates.enterContentPlaceholder')}
              className={`w-full p-2.5 rounded-xl border bg-surface text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 transition-all resize-y ${
                hasError
                  ? 'border-rose-500 ring-rose-500/20'
                  : 'border-surface-border focus:ring-primary/20 focus:border-primary'
              }`}
            />
            {hasError && (
              <p className="text-[11px] text-rose-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{errorMsg}</span>
              </p>
            )}
          </div>
        );

      case 'select': {
        const options = field.options || [];
        return (
          <div key={field.name} className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-rose-500">*</span>}
            </label>
            {field.description && (
              <p className="text-[11px] text-foreground-muted">{field.description}</p>
            )}
            <div className="relative">
              <select
                value={String(val || '')}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                onBlur={() => handleBlur(field.name)}
                className={`w-full p-2.5 pr-8 rounded-xl border bg-surface text-xs text-foreground focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${
                  hasError
                    ? 'border-rose-500 ring-rose-500/20'
                    : 'border-surface-border focus:ring-primary/20 focus:border-primary'
                }`}
              >
                {options.map((opt, idx) => {
                  const optLabel = typeof opt === 'string' ? opt : opt.label;
                  const optVal = typeof opt === 'string' ? opt : opt.value;
                  return (
                    <option key={idx} value={optVal}>
                      {optLabel}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="absolute right-3 top-3 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
            </div>
            {hasError && (
              <p className="text-[11px] text-rose-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{errorMsg}</span>
              </p>
            )}
          </div>
        );
      }

      case 'number':
        return (
          <div key={field.name} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-rose-500">*</span>}
              </label>
              {field.validation && (
                <span className="text-[10px] text-foreground-muted font-mono">
                  Min: {field.validation.min ?? 0}
                  {field.validation.max !== undefined ? ` • Max: ${field.validation.max}` : ''}
                </span>
              )}
            </div>
            {field.description && (
              <p className="text-[11px] text-foreground-muted">{field.description}</p>
            )}
            <input
              type="number"
              value={val !== undefined ? Number(val) : ''}
              onChange={(e) => handleFieldChange(field.name, Number(e.target.value))}
              onBlur={() => handleBlur(field.name)}
              min={field.validation?.min}
              max={field.validation?.max}
              className={`w-full p-2.5 rounded-xl border bg-surface text-xs text-foreground focus:outline-none focus:ring-2 transition-all ${
                hasError
                  ? 'border-rose-500 ring-rose-500/20'
                  : 'border-surface-border focus:ring-primary/20 focus:border-primary'
              }`}
            />
            {hasError && (
              <p className="text-[11px] text-rose-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{errorMsg}</span>
              </p>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div key={field.name} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-rose-500">*</span>}
              </label>
              {field.validation?.max && (
                <span className="text-[10px] text-foreground-muted font-mono">
                  {String(val || '').length} / {field.validation.max}
                </span>
              )}
            </div>
            {field.description && (
              <p className="text-[11px] text-foreground-muted">{field.description}</p>
            )}
            <input
              type="text"
              value={String(val || '')}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              placeholder={
                field.placeholder ||
                t('templates.enterFieldPlaceholder', { field: field.label.toLowerCase() })
              }
              className={`w-full p-2.5 rounded-xl border bg-surface text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 transition-all ${
                hasError
                  ? 'border-rose-500 ring-rose-500/20'
                  : 'border-surface-border focus:ring-primary/20 focus:border-primary'
              }`}
            />
            {hasError && (
              <p className="text-[11px] text-rose-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{errorMsg}</span>
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* 1. Global AI Writer Parameters Toolbar (Phase 4 Task 4.1) */}
      <div className="p-3.5 rounded-2xl border border-surface-border bg-surface-elevated/70 space-y-3.5">
        {/* Row A: Output Language & Tone of Voice */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Output Language */}
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-primary" />
              <span>{t('templates.form.outputLanguage')}</span>
            </label>
            <div className="relative">
              <select
                value={outputLanguage}
                onChange={(e) => setOutputLanguage(e.target.value)}
                className="w-full p-2 pr-8 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
              >
                {OUTPUT_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
            </div>
          </div>

          {/* Tone of Voice (27 Tones) */}
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
              <Smile className="w-3.5 h-3.5 text-primary" />
              <span>{t('templates.form.toneOfVoice')}</span>
            </label>
            <div className="relative">
              <select
                value={toneOfVoice}
                onChange={(e) => setToneOfVoice(e.target.value)}
                className="w-full p-2 pr-8 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
              >
                {TONES_OF_VOICE.map((tone) => (
                  <option key={tone} value={tone}>
                    {t(`templates.tones.${tone}` as any, undefined, tone)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Row B: Output Length Budget Tiers */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5 text-primary" />
              <span>{t('templates.form.outputLength')}</span>
            </label>
            <span className="text-[10px] font-mono text-primary font-semibold">
              {t('templates.form.tokensCount', { count: effectiveMaxTokens })}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-1.5 p-1 rounded-xl bg-surface border border-surface-border">
            {(['short', 'medium', 'long', 'custom'] as const).map((tier) => (
              <button
                key={tier}
                type="button"
                onClick={() => setOutputLength(tier)}
                className={`py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all text-center ${
                  outputLength === tier
                    ? 'bg-primary text-white shadow-sm font-semibold'
                    : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
                }`}
              >
                {t(`templates.output_lengths.${tier}` as any, undefined, tier)}
              </button>
            ))}
          </div>

          {outputLength === 'custom' && (
            <div className="pt-2 px-1 space-y-1 animate-in fade-in duration-150">
              <div className="flex items-center justify-between text-[10px] text-foreground-muted">
                <span>{t('templates.form.customTokens')}</span>
                <span className="font-mono font-bold text-primary">{customMaxTokens} tokens</span>
              </div>
              <input
                type="range"
                min="100"
                max="4000"
                step="50"
                value={customMaxTokens}
                onChange={(e) => setCustomMaxTokens(parseInt(e.target.value, 10))}
                className="w-full accent-primary h-1.5 bg-muted rounded-lg cursor-pointer"
              />
            </div>
          )}
        </div>

        {/* Row C: Focus Keywords Tag Input */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-primary" />
            <span>{t('templates.form.focusKeywords')}</span>
          </label>
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              placeholder={t('templates.form.focusKeywordsPlaceholder')}
              className="flex-1 p-2 rounded-xl border border-surface-border bg-surface text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              disabled={!keywordInput.trim()}
              className="p-2 rounded-xl border border-surface-border bg-surface hover:bg-muted/80 text-foreground text-xs font-semibold transition-colors disabled:opacity-40"
              title={t('templates.form.addKeywordBtn')}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          {focusKeywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {focusKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 animate-in fade-in duration-100"
                >
                  <span>{kw}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(kw)}
                    className="hover:opacity-75 focus:outline-none"
                    aria-label={t('common.delete')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 2. Dynamic Template Schema Fields */}
      <div className="space-y-4">
        {template.inputSchema.map((field) => renderField(field))}
      </div>

      {/* 3. Advanced AI Model & Parameters Accordion */}
      <div className="pt-2 border-t border-surface-border/60">
        <button
          type="button"
          onClick={() => setShowAdvanced((prev) => !prev)}
          className="flex items-center justify-between w-full text-xs font-semibold text-foreground-muted hover:text-foreground py-1 transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <SlidersHorizontal className="w-3.5 h-3.5 text-primary" />
            {t('templates.customizeModelAndTemp')}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-200 ${
              showAdvanced ? 'rotate-180' : ''
            }`}
          />
        </button>

        {showAdvanced && (
          <div className="mt-3 p-3.5 rounded-xl border border-surface-border bg-surface-elevated space-y-3.5 animate-in fade-in duration-150">
            {/* AI Model Selector */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                <Cpu className="w-3 h-3 text-primary" />
                {t('templates.aiModelInference')}
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full p-2 rounded-lg border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="gpt-4o">{t('templates.modelGpt4oDesc')}</option>
                <option value="claude-3-5-sonnet">{t('templates.modelClaudeDesc')}</option>
                <option value="gemini-1-5-pro">{t('templates.modelGeminiDesc')}</option>
                <option value="deepseek-r1">{t('templates.modelDeepseekDesc')}</option>
              </select>
            </div>

            {/* Creativity Temperature */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-foreground">
                  {t('templates.creativityLevel')}
                </span>
                <span className="font-mono text-primary font-bold">{temperature}</span>
              </div>
              <input
                type="range"
                min="0.1"
                max="1.2"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full accent-primary h-1.5 bg-muted rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-[9px] text-foreground-muted font-mono">
                <span>{t('templates.precise')}</span>
                <span>{t('templates.balanced')}</span>
                <span>{t('templates.creative')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 4. Live Interpolated Prompt Preview Toggle */}
      <div className="pt-2 border-t border-surface-border/60">
        <button
          type="button"
          onClick={() => setShowPromptPreview((prev) => !prev)}
          className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline"
        >
          {showPromptPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          <span>
            {showPromptPreview ? t('templates.hideRawPrompt') : t('templates.previewRawPrompt')}
          </span>
        </button>

        {showPromptPreview && (
          <div className="mt-2 p-3 rounded-xl border border-surface-border bg-muted/60 space-y-2 animate-in fade-in duration-150">
            <div className="text-[10px] font-mono font-semibold text-foreground-muted flex items-center gap-1">
              <Info className="w-3 h-3 text-primary" />
              <span>{t('templates.userPromptSentToLlm')}</span>
            </div>
            <pre className="text-[11px] font-mono text-foreground whitespace-pre-wrap max-h-36 overflow-y-auto leading-relaxed">
              {livePreview.systemPrompt}
              {'\n\n--- User Prompt ---\n'}
              {livePreview.userPrompt || t('templates.noContentEntered')}
            </pre>
          </div>
        )}
      </div>

      {/* 5. Action Buttons */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleResetForm}
          disabled={isGenerating}
          title={t('templates.resetFormTooltip')}
          className="p-2.5 rounded-xl border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" />
        </button>

        <button
          type="submit"
          disabled={isGenerating}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-semibold bg-gradient-to-r from-primary to-accent text-white shadow-glow-primary/20 hover:opacity-95 transition-all disabled:opacity-60 cursor-pointer"
        >
          <SophiaIcon name="algorithm" size={16} className="text-white shrink-0 animate-spin-slow" />
          <span>
            {isGenerating
              ? t('templates.generatingOutputAction')
              : t('templates.generateOutputAction')}
          </span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-mono text-[10px] bg-white/20 text-white font-bold">
            <SophiaIcon name="battery" size={11} className="text-white shrink-0" />
            {template.isPremium ? '2' : '1'} {t('wallet.credits')}
          </span>
        </button>
      </div>
    </form>
  );
};
